import { lazy, type ReactNode, Suspense, useMemo } from "react";
import { Navigate, useLocation } from "react-router";
import { holdsAny, ROLES } from "../lib/access";
import { useT } from "../shell/i18n";
import { AiLicenseProvider } from "../ui/ai-license";
import { usePhotograph } from "../ui/avatar";
import { ModuleNavProvider } from "../ui/module-nav";
import { PersonalizationProvider } from "../ui/personalization";
import { RecordLinkLocation } from "../ui/record-link";
import { TablePersonalizationProvider } from "../ui/table-personalization";
import { TooltipProvider } from "../ui/tooltip";
import { WorkingDraftProvider } from "../ui/working-draft";
import { ApplicationRoutes } from "./application-routes";
import { creatable } from "./creations";
import {
  canReach,
  canReachPath,
  groupsFor,
  moduleFor,
  moduleIsEnabled,
  narrowedFor,
  type RailGroup,
} from "./destinations";
import { NotFound } from "./not-found";
import { NotificationUpdates } from "./notification-updates";
import { participantOnboardingRedirect } from "./onboarding-locations";
import { PageQueryProvider } from "./page-query";
import { recordAddressesIn, recordOpenAt } from "./records";
import type { SignInState } from "./session";
import { Frame } from "./shell-frame";
import { Overlays } from "./shell-overlays";

const RecordPane = lazy(() =>
  import("./record-pane").then((module) => ({ default: module.RecordPane })),
);

function RecordPaneSlot({
  state,
  roles,
  mine,
}: {
  readonly state: unknown;
  readonly roles: readonly string[];
  readonly mine: string | null;
}) {
  // Rendering a lazy component is what starts its import. The pane is absent on almost every
  // frame of a session, so mounting it closed would still download and evaluate every record
  // body during startup. History state is the authoritative open signal; only an actual quick
  // view pays for the record surface. A missing fallback is intentional for the same measured
  // reason ordinary reads wait 200 ms before drawing a loading state: the page remains complete
  // underneath and the pane enters only when its code is ready.
  // Use the RAW namespaced addresses for the mount decision. `RecordPane` is also the owner
  // that removes a malformed or stale address from history; filtering first would skip that
  // owner and strand corrupt state forever. A plain page still has no addresses and pays zero.
  if (recordAddressesIn(state).length === 0) return null;
  return (
    <Suspense fallback={null}>
      <RecordPane roles={roles} mine={mine} />
    </Suspense>
  );
}

/**
 * THE SHELL IS FOUR COMPONENTS AND IT USED TO BE ONE, and the split is the fix rather
 * than a tidying.
 *
 * Measured at 4x CPU throttling before it: press → the pane's first motion frame was
 * **284ms opening and 278ms closing**, and closing it spent **one 248ms synchronous task**
 * — `workLoopSync → Shell` — before the browser could paint a single frame of a transition
 * that is otherwise clean (`compositeFailed: 0`, no gap over 9ms). The cause is one
 * sentence: **the component that held every pane's open state was the component that
 * rendered the page under it**, so a create flow that navigates nowhere re-rendered
 * `/people` and its whole table, and a record pane did the same through `useLocation`.
 *
 * The four are split by WHAT MAKES THEM RE-RENDER, which is the only division that buys
 * anything:
 *
 *   `Shell`     the session. Re-renders when the signed-in person's photograph resolves,
 *               and then never again for the life of the tab.
 *   `Located`   the address. Re-renders on every navigation, including one that only
 *               opens a record — and publishes `here`, the PAGE, which such a navigation
 *               does not move.
 *   `Overlays`  what is open over the page: the palette, the create flows, the toaster.
 *               Re-renders on every open and close.
 *   `Frame`     the grid — topbar, both rails, the module sidebar and the router. The
 *               expensive one, and the only one that must not re-render for any of the
 *               above.
 *
 * WHAT KEEPS `Frame` STILL IS THAT ITS ELEMENT IS `Shell`'s, threaded down as `children`.
 * `Located` and `Overlays` re-render and hand on the SAME element object, so React bails
 * out of the subtree rather than reconciling into it — which is a property of the tree's
 * shape and not of a `memo` somebody has to remember. The one thing `Frame` subscribes to
 * is `useModuleNav()`, whose value is already memoized on the module, the page address and
 * the collapse state: a record pane opening moves none of the three, so the value is
 * `Object.is`-equal and nothing under here is asked to render at all.
 *
 * The corollary, and the rule to keep: **a `useState` added to `Shell` puts the defect
 * back.** State belongs in the component that is allowed to re-render for it.
 */
export function ApplicationShell({
  state,
}: {
  readonly state: Extract<SignInState, { status: "signed_in" }>;
}) {
  const t = useT();
  // WHAT THIS PERSON CAN REACH, from the session's roles and one declaration.
  //
  // This asked `scopes.includes("riksu.write")`, which every ordinary employee's token
  // carries — it is what lets them request their own leave. So the rail showed the
  // administration group to everybody, and every one of them who clicked it met a
  // refusal the screen had promised would not happen.
  //
  // The requirement now comes from `destinations.ts` in the same vocabulary the server
  // enforces, so "can I reach this" is answered rather than guessed.
  const groups = groupsFor(state.riksu);
  // Whether the people LIST widens to the whole company is a separate question from
  // whether the destination is reachable: a line manager reaches /people and sees
  // their subtree, while an HR administrator reaches it and sees everybody.
  const administering = holdsAny(state.riksu.roles, ROLES.people);

  const who = state.riksu.person ? state.riksu.person.display_name : t("session.notAnEmployee");

  // THE ONE PHOTOGRAPH THE SHELL ASKS FOR, and it asks for it HERE rather than inside the
  // account menu. This shell is mounted for the whole session; a read inside a component
  // that re-renders often is a read per re-render. `usePhotograph` keys on the id, so it
  // runs once and again only if the signed-in person changes, which is never within a
  // session.
  //
  // AND THIS IS NOW THE ONLY THING THAT RE-RENDERS `Shell`, which is why it is the right
  // component for it: resolving it rebuilds `Frame` once, at the moment a face arrives,
  // rather than on every navigation and every pane.
  //
  // `?? null` and not `undefined`: the hook takes `string | null` because "there is
  // nobody to ask about" is an answer a support engineer's session gives every time, and
  // it must not read as "not asked yet".
  const mine = usePhotograph(state.riksu.person?.person_id ?? null);

  // The palette's destinations ARE the navigation's, from the same declaration. One
  // list, so a screen added to the rail is findable without anybody remembering to add
  // it twice — and one that a caller cannot reach is in neither.
  const destinations = groups.flatMap((group) =>
    group.items.map((item) => ({ to: item.to, label: item.label, group: group.label })),
  );

  // WHAT THIS PERSON CAN CREATE, from one declaration, narrowed the same way the rail is.
  // The `+` in the topbar and the palette both read this array — two lists would drift,
  // and the palette is where somebody looks for the thing missing from the menu.
  const creations = creatable(state.riksu);

  return (
    // ONE PROVIDER, at the root. Without it every tooltip waits its own delay in
    // isolation and none of them share the behaviour that makes a strip of icons feel
    // fast: once one is open, crossing to its neighbour shows the next IMMEDIATELY.
    // Base UI signals that with `data-instant`, `tooltipMotion` already branches on it,
    // and the signal never fired because nothing was listening.
    <AiLicenseProvider
      enabled={state.riksu.modules.includes("ai")}
      features={state.riksu.ai_features ?? []}
    >
      <WorkingDraftProvider
        owner={state.riksu}
        messagingEnabled={state.riksu.modules.includes("messaging")}
        performanceEnabled={
          state.riksu.modules.includes("performance") && Boolean(state.riksu.person)
        }
        serviceEnabled={state.riksu.modules.includes("people") && Boolean(state.riksu.person)}
        enabled={
          state.riksu.modules.includes("recruiting") &&
          holdsAny(state.riksu.roles, ROLES.requisitionAuthors)
        }
        interviewEnabled={
          state.riksu.modules.includes("recruiting") &&
          holdsAny(state.riksu.roles, ROLES.recruitingEvaluators)
        }
      >
        <TooltipProvider>
          {/* During a rolling deployment an already-open browser can briefly meet the old
          bootstrap shape. Treating the absent presentation layer as the product default
          keeps that mixed-version window usable; it grants nothing and is replaced on the
          next session read. */}
          <PersonalizationProvider>
            <TablePersonalizationProvider initial={state.riksu.table_personalizations ?? []}>
              <Located state={state} groups={groups}>
                {/* THE PRINCIPAL REACHES THE CREATE PANES. A flow the shell mounts stands on no
            screen, which is why it may be handed no screen state — and it was reading that
            as *may be handed nothing*, so `NewLeaveRequest` hard-coded `personId={null}`
            and the `+` opened a leave form that could not tell the reader from a colleague.
            A string, so `Overlays` re-renders for it exactly as often as `Shell` does, which
            is once a session: the frame's element is still `Shell`'s and is still handed on
            untouched. See `CreateFlowProps.personId`. */}
                <Overlays
                  destinations={destinations}
                  creations={creations}
                  aiEnabled={state.riksu.modules.includes("ai")}
                  personId={state.riksu.person?.person_id ?? null}
                  roles={state.riksu.roles ?? []}
                >
                  {/* THE ELEMENT THAT MUST NOT BE REBUILT. It is created here, in the component
              that re-renders once a session, and handed down through two that re-render
              constantly — so a navigation that opens a record and a press that opens a
              create pane both leave it referentially identical and React skips it whole.
              Passing these as props rather than reading them from a context is the same
              decision: a context value that moved would defeat the bail-out it exists to
              enable. */}
                  <NotificationUpdates>
                    <Frame
                      state={state}
                      groups={groups}
                      who={who}
                      photograph={mine}
                      creations={creations}
                    >
                      <ApplicationRoutes
                        state={state}
                        administering={administering}
                        photograph={mine}
                      />
                    </Frame>
                  </NotificationUpdates>
                </Overlays>
              </Located>
            </TablePersonalizationProvider>
          </PersonalizationProvider>
        </TooltipProvider>
      </WorkingDraftProvider>
    </AiLicenseProvider>
  );
}

/**
 * WHERE THE READER IS, resolved once and published as the PAGE.
 *
 * This is the component that subscribes to the router, and it is deliberately the only one
 * in the shell that does. `useLocation` re-renders its caller on every navigation — there
 * is no selector form — so a shell that called it drew the whole product again every time
 * a pane changed the router entry. Here the subscription is isolated: `Located` re-renders,
 * while `here` stays the page pathname when quick-view state changes. Opening a person over
 * `/people` therefore adds a history entry without moving the page or rebuilding its tree.
 *
 * `children` is `Shell`'s element and is passed straight through. That is what makes the
 * sentence above true of the FRAME as well as of the context: a component that re-renders
 * and hands on the same child does not re-render that child.
 */
function Located({
  state,
  groups,
  children,
}: {
  readonly state: Extract<SignInState, { status: "signed_in" }>;
  /**
   * THE RAIL'S OWN GROUPS, so the sidebar can draw the AREA a module sits in.
   *
   * Handed down from `Shell` rather than recomputed here, and that is the point rather than
   * a saving: `groupsFor` narrows by role, so a second call would be a second answer to
   * *what can this person reach* — the defect `destinations.ts` exists to prevent, arriving
   * between the rail and the sidebar. It is also referentially stable for the session, so
   * adding it to `ModuleNavProvider`'s memo costs nothing that `shell-render.test.tsx`
   * measures.
   */
  readonly groups: readonly RailGroup[];
  readonly children: ReactNode;
}) {
  // WHICH MODULE IS OPEN, and therefore what the second sidebar holds and what the page
  // header says you are inside.
  //
  // Resolved HERE rather than by each screen, so a screen cannot declare a set of
  // siblings that disagrees with the rail's — which is the defect `destinations.ts`
  // exists to prevent one level up, and the same one repeats at this level the moment
  // two places know the answer.
  //
  // Filtered by what the caller can reach: a section they would be refused on arrival
  // is the link-that-always-refuses defect one level down from the rail, and the rail
  // already learned it. This one line is what keeps an unreachable module out of the
  // breadcrumb as well as out of the sidebar, since both read the answer rather than the
  // address.
  const location = useLocation();
  // WHERE THE READER IS, and it is the PATHNAME — nothing derived, nothing preferred.
  //
  // Everything the shell draws behind the scrim reads this: the router below, the module
  // sidebar, the section highlight, the breadcrumb, and the address a reference pressed
  // inside the pane opens over. They are one question with one answer, and a sidebar saying
  // Kişiler over a gap report is the same defect as the gap report vanishing.
  //
  // **IT MUST STAY A BARE PATHNAME.** `moduleFor` consumes routes, not query or fragment
  // data. Quick-view state does not change this value.
  //
  // THE GATE IS `records.test.tsx`'s "the module sidebar keeps the PAGE while a record is
  // open over it", and until 2026-08-12 there was none — this comment named one, by a
  // string (`the shell publishes a bare pathname`) that appeared nowhere in the tree, and
  // claimed a coverage figure of "eight reds across two files" that was measured at ONE.
  // That one was `shell-render.test.tsx`'s re-render count, which reddens here for a reason
  // unrelated to navigation and names nothing.
  //
  // Measured by planting exactly that, `here` composed from the pathname AND the search:
  // over `/leave` the sidebar answers `["Leave"]` where it answers `["Leave", "Balances"]`
  // — and the SURVIVOR was the point. That reading was taken while the rail entry was a
  // `NavLink`, which decides for itself from the router and went on lighting correctly with
  // this line broken, leaving the section — a plain `Link` reading the provider's `at` — as
  // the only live probe. **The rail entry is a `Link` now too**, reading the same `at`
  // through `moduleFor`, so it is a second probe rather than a survivor and the same
  // mutation darkens both. The gate below is unchanged and is now covered twice; what is no
  // longer true is that a page declaring NO sections (`/people`) would pass with this rule
  // broken, so the reason for choosing `/leave` is weaker than it was and the choice still
  // stands — the section link is the narrower witness.
  //
  // A `?` and a `#` break it identically. The gate asserts the SHAPE — what the sidebar
  // lights — rather than the character, so it covers both without naming either.
  //
  // The pane deliberately reads the namespaced STATE of the same history entry. That split
  // is the whole design: the path decides which page, state decides which quick views are
  // open, and the page's own query remains the page's own query.
  const here = location.pathname;
  const found = useMemo(() => moduleFor(here), [here]);
  // A RECORD IS NOT ITS REGISTER. `/documents` is an administrative queue, while
  // `/documents/{id}` may name the signed-in person's own file; the same distinction
  // exists between the asset catalogue and a worker's custody record. Applying the
  // register's role gate to the durable record address turns a server-authorised read
  // into a client-side 404 before the record endpoint can answer.
  //
  // The tenant module remains a courtesy boundary: a disabled product must not issue a
  // read that can only fail. Within an enabled module, the record endpoint owns the
  // person/role/oracle decision. Navigation remains narrower for the register itself.
  const directRecord = recordOpenAt(here) !== null;
  const allowed = found
    ? directRecord
      ? moduleIsEnabled(found, state.riksu)
      : canReachPath(found, here, state.riksu)
    : true;
  const open = useMemo(() => {
    // NARROWED TWICE, and the two narrowings answer two different questions. `canReach` asks
    // whether the MODULE is this caller's at all; `narrowedFor` drops the SCREENS inside it
    // that are not — which `/recruiting` is the first module to need, because its pipeline
    // and its seat half are two server role sets. It answers the same object where nothing
    // is narrowed, so the memo below it keeps its identity for the other nineteen.
    // A directly assigned child record may be reachable while its register is not. In that
    // case the record endpoint owns the final subject decision and the shell must not expose
    // the module sidebar beside it: those links are still outside this caller's role set.
    return found && allowed && canReach(found, state.riksu)
      ? narrowedFor(found, state.riksu)
      : undefined;
  }, [allowed, found, state.riksu]);

  // THE AREA THAT MODULE SITS IN, found in the rail's own list rather than resolved again.
  //
  // `groupsFor` has already dropped what this caller cannot reach, so a module found here is
  // a module the sidebar may list — and the rail and the sidebar are reading ONE narrowing
  // rather than two that agree until a role changes. `undefined` for every module in a group
  // that does not collapse; the provider normalises that to `null`.
  const area = useMemo(
    () => (open ? groups.find((group) => group.label === open.group) : undefined),
    [open, groups],
  );

  // A direct address is still a courtesy boundary. The server independently refuses every
  // licensed operation, but rendering a whole module whose tenant does not hold it would
  // immediately issue requests that can only fail. Use the same unaddressable answer as an
  // unknown route so the browser does not disclose which optional product exists here.
  const onboardingTarget = participantOnboardingRedirect(here, state.riksu.roles ?? []);
  if (onboardingTarget) {
    return (
      <Navigate
        to={{ pathname: onboardingTarget, search: location.search, hash: location.hash }}
        replace
      />
    );
  }
  if (found && !allowed) {
    return <NotFound />;
  }

  return (
    /* The provider takes the MODULE, the AREA it belongs to and the address it is at —
       `module-nav.tsx` derives the sections from the same declaration the trail does, rather
       than being handed a list. Its value is memoized on exactly those, which is what lets
       everything below skip a navigation that only opened a record. */
    <ModuleNavProvider module={open ?? null} group={area ?? null} at={here}>
      <RecordLinkLocation location={location}>
        {/* ONE DETAIL PANE FOR THE WHOLE PRODUCT, driven by namespaced history state rather
          than by a screen's local state. It is here rather than inside `<Routes>` for the reason it
          exists at all: a screen that mounted its own would be a screen writing a pane,
          and the second one would differ from the first. Every declared reference in
          every table opens THIS, and what it holds comes from the current history entry. See
          `records.ts`.

          IT SITS IN `Located` BECAUSE THAT COMPONENT OWNS THE ROUTER SUBSCRIPTION. It reads
          the actual `location.state`, so it should re-render when the history entry changes,
          and it is mounted beside the page tree that should not. */}
        {/* THE ROLES GO IN, for the reason they go into every other surface here: a record
          is a READ with exactly one write on it — a seat's department — and offering the
          pencil to somebody riksu will refuse is the link-that-always-refuses defect one
          control down. riksu decides regardless. */}
        {/* THE PRINCIPAL GOES IN TOO, for the reason it goes into `/leave`, `/expense` and
          `/time`: a record body cannot tell the reader's own record from a stranger's,
          and the claim's pane needs exactly that to decide whether "whose claim is this"
          is worth a line. `null` is ordinary — a support engineer, a partner
          administrator — and reads as "every record is somebody else's". */}
        <RecordPaneSlot
          state={location.state}
          roles={state.riksu.roles ?? []}
          mine={state.riksu.person?.person_id ?? null}
        />
        <PageQueryProvider search={location.search} hash={location.hash}>
          {children}
        </PageQueryProvider>
      </RecordLinkLocation>
    </ModuleNavProvider>
  );
}
